<template>
    <div class="summary-history-list-root">
        <div v-if="!hasSelection" class="summary-history-empty">
            <span v-t="'message.info_no_summary_selection'"></span>
        </div>
        <template v-else>
            <div class="summary-history-header">
                <button
                    class="summary-history-close-button"
                    type="button"
                    @click="$emit('toggle-summary')">
                    <span v-t="'message.title_summaryHistory'"></span><i class="el-icon-arrow-right"></i>
                </button>
            </div>
            <!-- 左側（通話履歴一覧）のツールバー行と高さを揃える。左側は言語選択の el-select を含み、
                 収まりきらない幅では折り返して行が伸びることがあるため、padding で高さを真似るのではなく
                 このボタン単体の行の高さを明示的に固定する -->
            <div class="object-titlebox">
                <div class="object-titlebox-right summary-history-toolbar">
                    <!-- 一覧が既定。行クリックか生成完了ポップアップから埋め込み詳細に切り替わり、
                         一覧の取り直しは一覧側でだけ行い、埋め込み側には更新ボタンを置かない -->
                    <template v-if="!listViewMode">
                        <button
                            class="transparent-button summary-back-to-list-button"
                            type="button"
                            v-on:click="showList()">
                            <img src="../../assets/icon_return.svg"/><span class="normal-command-text" v-t="'message.action_summary_back_to_list'"></span>
                        </button>
                        <button
                            v-if="detailRecord"
                            class="transparent-button"
                            type="button"
                            v-on:click="openDetailDialog()">
                            <i class="el-icon-full-screen summary-expand-icon"></i>
                        </button>
                    </template>
                    <button
                        v-else
                        class="transparent-button"
                        type="button"
                        :disabled="loading"
                        v-on:click="refresh()">
                        <img src="../../assets/icon_load.svg"/><span class="normal-command-text" v-t="'message.action_summary_refresh'"></span>
                    </button>
                </div>
            </div>
            <div v-if="!isTalkgroup" class="summary-history-empty">
                <span v-t="'message.info_summary_group_only'"></span>
            </div>
            <div v-else-if="!listViewMode"
                 class="summary-latest-root"
                 v-loading="loading"
                 element-loading-text="Loading..."
                 element-loading-spinner="el-icon-loading"
                 element-loading-background="rgba(0, 0, 0, 0.8)">
                <div v-if="!detailRecord" class="summary-history-empty">
                    <span v-if="!loading" v-t="'message.info_no_summary_record'"></span>
                </div>
                <template v-else>
                    <div class="summary-detail-panel">
                        <!-- 保存はタイトル欄の隣、コピーは要約結果の見出しの隣に置き、何に対する操作かを
                             配置で示す（画面最下部にまとめない） -->
                        <div class="summary-latest-title-row">
                            <div class="detail-title-wrap">
                                <input class="detail-title-input" v-model="detailTitleDraft" :placeholder="$t('message.item_summary_title')" />
                                <img src="../../assets/edit.svg" class="detail-title-edit-icon"/>
                            </div>
                            <el-button
                                type="primary"
                                size="small"
                                :loading="savingTitle"
                                :disabled="!titleDirty || savingTitle"
                                @click="saveTitle()"
                                v-t="'message.action_summary_save_title'">
                            </el-button>
                        </div>
                        <p class="summary-detail-meta">{{ detailRecord.periodText }}</p>
                        <el-collapse v-model="speakerAccordionActive" class="speaker-accordion">
                            <el-collapse-item v-if="detailRecord.speakerNames && detailRecord.speakerNames.length" name="speakers">
                                <template slot="title">
                                    <span>{{ $t('message.item_summary_history_speakers_count', { count: detailRecord.speakerNames.length }) }}</span>
                                    <i class="speaker-accordion-icon" :class="speakerAccordionActive.includes('speakers') ? 'el-icon-caret-top' : 'el-icon-caret-bottom'"></i>
                                </template>
                                <span v-for="name in detailRecord.speakerNames" :key="name" class="speaker-chip">{{ name }}</span>
                            </el-collapse-item>
                            <!-- 生成に使ったプロンプト。見出しに名前を出し、開くと本文を確認できる -->
                            <el-collapse-item name="prompt">
                                <template slot="title">
                                    <span class="summary-prompt-title">{{ $t('message.item_summary_prompt') }}: {{ promptNameText }}</span>
                                    <i class="speaker-accordion-icon" :class="speakerAccordionActive.includes('prompt') ? 'el-icon-caret-top' : 'el-icon-caret-bottom'"></i>
                                </template>
                                <pre v-if="detailRecord.promptText" class="summary-prompt-text">{{ detailRecord.promptText }}</pre>
                                <span v-else class="summary-prompt-missing" v-t="'message.item_summary_unknown'"></span>
                            </el-collapse-item>
                        </el-collapse>
                        <div class="summary-latest-result-header">
                            <span class="readonly-label" v-t="'message.item_summary_result'"></span>
                            <el-button size="mini" @click="copyDetail()" v-t="'message.action_summary_copy_result'"></el-button>
                        </div>
                        <textarea class="summary-detail-text summary-latest-text" :value="detailRecord.summaryText" readonly></textarea>
                    </div>
                </template>
            </div>
            <template v-else>
                <div class="summary-history-table-wrap">
                    <el-table
                        v-loading="loading"
                        element-loading-text="Loading..."
                        element-loading-spinner="el-icon-loading"
                        element-loading-background="rgba(0, 0, 0, 0.8)"
                        :empty-text="$t('message.info_no_summary_record')"
                        :data="records"
                        height="100%"
                        style="width: 100%"
                        border
                        @row-click="openDetail">
                        <!-- border は列幅をヘッダ境界のドラッグで変えられるようにするためで、罫線自体は CSS で消している -->
                        <el-table-column prop="periodText" :label="$t('message.item_summary_period')" width="120" class-name="summary-history-period-cell">
                            <template slot-scope="scope">
                                <div v-for="(line, index) in periodLines(scope.row.periodText)" :key="index" class="summary-history-period-line">{{ line }}</div>
                            </template>
                        </el-table-column>
                        <el-table-column prop="title" :label="$t('message.item_summary_title')" header-align="left" align="left"></el-table-column>
                        <el-table-column width="40" align="center">
                            <template slot-scope="scope">
                                <img src="../../assets/icon_detail.svg" class="summary-history-detail-icon"/>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>
            </template>
        </template>

        <transition name="modal">
        <div class="dialog-modal-mask" v-if="detailDialogVisible && detailRecord">
        <div class="small-panel summary-detail-dialog" @click.stop>
            <div class="detail-title-row">
                <div class="detail-title-wrap">
                    <input class="detail-title-input" v-model="detailTitleDraft" :placeholder="$t('message.item_summary_title')" />
                    <img src="../../assets/edit.svg" class="detail-title-edit-icon"/>
                </div>
            </div>
            <hr class="dialog-hr">
            <div class="dialog-body-small">
                <p class="summary-detail-meta">{{ detailRecord.periodText }}</p>

                <!-- Element標準の矢印(右→回転)ではなく、閉:下三角/開:上三角にしたいのでタイトルを
                     slot 差し替えして自前のアイコンを出し、標準の矢印は非表示にする -->
                <el-collapse v-model="speakerAccordionActive" class="speaker-accordion">
                    <el-collapse-item v-if="detailRecord.speakerNames && detailRecord.speakerNames.length" name="speakers">
                        <template slot="title">
                            <span>{{ $t('message.item_summary_history_speakers_count', { count: detailRecord.speakerNames.length }) }}</span>
                            <i class="speaker-accordion-icon" :class="speakerAccordionActive.includes('speakers') ? 'el-icon-caret-top' : 'el-icon-caret-bottom'"></i>
                        </template>
                        <span v-for="name in detailRecord.speakerNames" :key="name" class="speaker-chip">{{ name }}</span>
                    </el-collapse-item>
                    <!-- 生成に使ったプロンプト。見出しに名前を出し、開くと本文を確認できる -->
                    <el-collapse-item name="prompt">
                        <template slot="title">
                            <span class="summary-prompt-title">{{ $t('message.item_summary_prompt') }}: {{ promptNameText }}</span>
                            <i class="speaker-accordion-icon" :class="speakerAccordionActive.includes('prompt') ? 'el-icon-caret-top' : 'el-icon-caret-bottom'"></i>
                        </template>
                        <pre v-if="detailRecord.promptText" class="summary-prompt-text">{{ detailRecord.promptText }}</pre>
                        <span v-else class="summary-prompt-missing" v-t="'message.item_summary_unknown'"></span>
                    </el-collapse-item>
                </el-collapse>

                <div class="readonly-label" v-t="'message.item_summary_result'"></div>
                <textarea class="summary-detail-text" :value="detailRecord.summaryText" readonly></textarea>

                <div class="copy-row">
                    <el-button size="small" @click="copyDetail()" v-t="'message.action_summary_copy'"></el-button>
                </div>
            </div>
            <hr class="dialog-hr">
            <div class="dialog-button-field summary-dialog-button-field">
                <el-button @click="closeDetailDialog()" v-t="'message.action_close'"></el-button>
                <el-button
                    class="dialog-ok-button"
                    :loading="savingTitle"
                    :disabled="!titleDirty || savingTitle"
                    @click="saveTitle()"
                    v-t="'message.button_save2'">
                </el-button>
            </div>
        </div>
        </div>
        </transition>
    </div>
</template>

<script>
import server from "../ServerApi.js";
import commonUtil from "../commonutil.js";

const PAGE_SIZE = 100;

export default {
    props: {
        selected: { type: Array, default: () => [] },
        isTalkgroup: { type: Boolean, default: true },
        appData: { type: Object, default: () => ({}) },
        fromDateTime: { type: Date, default: () => new Date() },
        toDateTime: { type: Date, default: () => new Date() },
    },
    data() {
        return {
            loading: false,
            allRecords: [],
            detailDialogVisible: false,
            speakerAccordionActive: [],
            detailRecord: null,
            detailTitleDraft: '',
            detailTitleAtOpen: '',
            detailNonce: 0,
            detailDialogNonce: 0,
            savingTitle: false,
            aiServerBaseUrl: "",
            fetchNonce: 0,
            // 一覧が既定。埋め込み詳細は行クリックか生成完了ポップアップから開く
            listViewMode: true,
        };
    },
    computed: {
        hasSelection() {
            return this.selected.length > 0;
        },
        targetNode() {
            return this.selected.length > 0 ? this.selected[0] : null;
        },
        // 一覧は作成日時の新しい順
        records() {
            return this.allRecords.slice().sort((a, b) => b.createdAt - a.createdAt);
        },
        // fromDateTime/toDateTimeは日時レンジピッカー(1コントロール)の値なので同時に変わる。
        // 別々のwatcherにすると1回の変更でrefresh()が2回走ってしまうため、1つの値にまとめて単一のwatcherにする
        dateRangeKey() {
            return this.fromDateTime.getTime() + '_' + this.toDateTime.getTime();
        },
        // ツリーでグループ/ユーザーを切り替えると selected と isTalkgroup が同時に変わる
        // (Talk.vue の handleOnSelectNode)。別々のwatcherにすると1回の選択でrefresh()が
        // 2回走ってしまうため、こちらも1つの値にまとめて単一のwatcherにする
        selectionKey() {
            const ids = this.selected.map(function (node) { return node.data.id; }).join(',');
            return this.isTalkgroup + '_' + ids;
        },
        promptNameText() {
            if (this.detailRecord && this.detailRecord.promptName) {
                return this.detailRecord.promptName;
            }
            return commonUtil.getMessageText(this.$i18n, "item_summary_unknown");
        },
        titleDirty() {
            return !!this.detailRecord
                && this.detailTitleDraft.trim() !== ''
                && this.detailTitleDraft !== this.detailRecord.title;
        },
    },
    watch: {
        selectionKey() {
            this.refresh();
        },
        dateRangeKey() {
            this.refresh();
        },
    },
    created() {
        this.refresh();
    },
    methods: {
        getTenantId() {
            if (this.appData && this.appData.tenantList && this.appData.tenantList.length > 0) {
                return this.appData.tenantList[0].tenantId;
            }
            const sessionTenantId = sessionStorage.getItem("tenantId");
            return sessionTenantId ? Number(sessionTenantId) : 0;
        },
        normalizeAiServerUrl(rawUrl) {
            const trimmed = String(rawUrl || "").replace(/\/+$/, "");
            if (!trimmed) {
                throw new Error("empty ai server url");
            }
            if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
                return trimmed;
            }
            return `https://${trimmed}`;
        },
        async loadAiServerUrlIfNeeded() {
            if (this.aiServerBaseUrl) {
                return;
            }
            const res = await server.post("/getaiserverurl", {});
            if (res.data.status !== 0) {
                throw new Error(`getaiserverurl failed status=${res.data.status}`);
            }
            this.aiServerBaseUrl = this.normalizeAiServerUrl(res.data.aiServerUrl);
        },
        refresh() {
            this.allRecords = [];
            this.fetchNonce++;
            // 別グループ/別期間に切り替えた際、前回の一覧に対する詳細が表示されたまま
            // 残ってしまう（一覧と表示内容が食い違う）のを防ぐ。showGenerated() はこの直後に
            // 生成結果の詳細を開き直すため、ここで閉じても問題ない
            this.clearDetail();
            // ユーザーごとの要約履歴は一旦非表示（グループ選択時のみ取得する）
            if (!this.hasSelection || !this.isTalkgroup) {
                return null;
            }
            return this.fetchPage();
        },
        // 発話履歴（TalkrecordList）は検索条件に一致する分を全件表示するのに対し、こちらは
        // API が limit/beforeId によるページングを返す（1回の応答は最大100件・hasMore で継続要否を示す）。
        // 1ページ目だけ見せると「20件を超えると古い要約が一覧から消えて見える」という取りこぼしになるため、
        // hasMore が false になるまで beforeId を進めながら全ページ取得して揃える
        async fetchPage() {
            const requestNonce = this.fetchNonce;
            this.loading = true;
            try {
                await this.loadAiServerUrlIfNeeded();
                const tenantId = this.getTenantId();
                const fromTime = this.fromDateTime.getTime();
                const toTime = this.toDateTime.getTime();
                let beforeId = null;
                let hasMore = true;
                while (hasMore) {
                    let path = `/console/summary/record/group?groupId=${this.targetNode.data.id}&fromTime=${fromTime}&toTime=${toTime}&limit=${PAGE_SIZE}`;
                    if (beforeId != null) {
                        path += `&beforeId=${beforeId}`;
                    }
                    const res = await server.aiGet(this.aiServerBaseUrl, path, tenantId);
                    // 取得中に対象・期間が変わっていたら（refresh 済み）この応答は捨てる
                    if (requestNonce !== this.fetchNonce) {
                        return;
                    }
                    const page = res.data.recordList || [];
                    this.allRecords = this.allRecords.concat(page);
                    hasMore = !!res.data.hasMore && page.length > 0;
                    if (hasMore) {
                        beforeId = page[page.length - 1].id;
                    }
                }
            } catch (error) {
                if (requestNonce !== this.fetchNonce) {
                    return;
                }
                commonUtil.putExceptionErrorMessage(this, "getSummaryRecords", error);
                commonUtil.notifyMessage(this, "Error", commonUtil.getMessageText(this.$i18n, "error_summary_history_load_failed"));
            } finally {
                if (requestNonce === this.fetchNonce) {
                    this.loading = false;
                }
            }
        },
        // サーバの periodText は「yyyy/MM/dd HH:mm〜HH:mm」。狭い列でも時刻まで読めるよう日付と時刻帯で行を分ける
        periodLines(periodText) {
            const text = periodText || '';
            const sep = text.indexOf(' ');
            return sep === -1 ? [text] : [text.slice(0, sep), text.slice(sep + 1)];
        },
        // 一覧の行クリック・生成完了のどちらからでも、埋め込み表示に切り替えて開く
        openDetail(record) {
            // 同じレコードを保存待ちの間に開き直すケースを区別するため、参照一致ではなく
            // nonce で「今開いている詳細」を識別する
            this.detailNonce++;
            this.detailRecord = record;
            this.detailTitleDraft = record.title;
            this.detailTitleAtOpen = record.title;
            this.speakerAccordionActive = [];
            this.listViewMode = false;
        },
        // 一覧へ戻る。行を選び直す前提なので表示中の詳細は捨てる
        showList() {
            this.clearDetail();
            this.listViewMode = true;
        },
        clearDetail() {
            this.detailNonce++;
            this.detailDialogVisible = false;
            this.detailRecord = null;
            this.detailTitleDraft = '';
            this.detailTitleAtOpen = '';
        },
        // 拡大ボタン。埋め込みで表示中の要約を同じ内容のダイアログで大きく見せる
        // 開閉のたびに nonce を進め、保存待ち中に閉じて開き直したダイアログを
        // 古い保存完了処理が閉じてしまわないようにする
        openDetailDialog() {
            if (this.detailRecord) {
                this.detailDialogNonce++;
                this.detailDialogVisible = true;
            }
        },
        closeDetailDialog() {
            this.detailDialogNonce++;
            this.detailDialogVisible = false;
        },
        // 生成直後は一覧を取り直して、そこに現れた行で詳細を開く。
        // 検索期間と重ならず一覧に出ない場合は生成結果そのものを表示する。
        // 要約ダイアログは発話履歴ペイン側にあるため、Talk.vue から呼ばれる
        showGenerated(generated) {
            const fetched = this.refresh();
            if (!fetched) {
                this.openDetail(generated);
                return;
            }
            fetched.then(() => {
                const stored = this.records.find((row) => row.id === generated.id);
                this.openDetail(stored || generated);
            });
        },
        copyDetail() {
            if (!this.detailRecord || !navigator.clipboard) {
                return;
            }
            navigator.clipboard.writeText(this.detailRecord.summaryText).then(() => {
                commonUtil.notifyMessage(this, "Success", commonUtil.getMessageText(this.$i18n, "info_summary_copied"));
            });
        },
        async saveTitle() {
            if (!this.detailRecord || !this.titleDirty || this.savingTitle) {
                return;
            }
            const targetRecord = this.detailRecord;
            const sessionNonce = this.detailNonce;
            const dialogNonce = this.detailDialogNonce;
            const newTitle = this.detailTitleDraft.trim();
            if (!newTitle) {
                return;
            }
            this.savingTitle = true;
            try {
                await this.loadAiServerUrlIfNeeded();
                const tenantId = this.getTenantId();
                await server.aiPost(this.aiServerBaseUrl, "/console/summary/record/title", {
                    id: targetRecord.id,
                    title: newTitle,
                }, tenantId);
                targetRecord.title = newTitle;
                const listedRecord = this.allRecords.find((r) => r.id === targetRecord.id);
                if (listedRecord) {
                    listedRecord.title = newTitle;
                }
                commonUtil.notifyMessage(this, "Success", commonUtil.getMessageText(this.$i18n, "info_summary_history_title_saved"));
                if (this.detailNonce === sessionNonce) {
                    // 埋め込み表示は閉じる先が無いので保存後の値を表示し続け、ダイアログから
                    // 保存した場合はダイアログだけ従来どおり閉じる
                    this.detailTitleAtOpen = newTitle;
                    if (this.detailDialogNonce === dialogNonce) {
                        this.closeDetailDialog();
                    }
                } else if (
                    this.detailRecord
                    && this.detailRecord.id === targetRecord.id
                    && this.detailTitleDraft === this.detailTitleAtOpen
                ) {
                    // 保存待ちの間に閉じて同じレコードを開き直したが、開き直してからは
                    // まだ何も編集していないケース。開き直し後に入力された未保存の編集を
                    // 消してしまわないよう、draftが開いた時のままの場合だけ保存済みの値に合わせる
                    this.detailTitleDraft = newTitle;
                    this.detailTitleAtOpen = newTitle;
                }
            } catch (error) {
                commonUtil.putExceptionErrorMessage(this, "saveTitle", error);
                commonUtil.notifyMessage(this, "Error", commonUtil.getMessageText(this.$i18n, "error_summary_history_title_save_failed"));
            } finally {
                this.savingTitle = false;
            }
        },
    },
};
</script>

<style scoped>
.summary-history-list-root {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}
.summary-history-empty {
    padding: 24px;
    color: #848991;
    text-align: center;
}
.summary-history-header {
    height: 40px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    background-color: #ecf1fa;
}
.summary-history-toolbar {
    height: 40px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 10px;
}
.summary-history-close-button {
    border: none;
    background-color: #f6f6f6;
    height: 100%;
    padding: 0 15px;
    margin: 0;
    color: #848991;
    font-size: 14px;
    cursor: pointer;
}
.summary-history-close-button i {
    margin-left: 4px;
    vertical-align: middle;
}
.summary-history-close-button:hover {
    opacity: 0.7;
}
.summary-history-table-wrap {
    flex: 1;
    min-height: 0;
}
.summary-history-detail-icon {
    width: 16px;
    height: 16px;
    vertical-align: middle;
    cursor: pointer;
}
.summary-history-table-wrap >>> .summary-history-period-line {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
/* 列幅ドラッグのために border を付けているが、見た目は従来の横罫線のみに揃える */
.summary-history-table-wrap >>> .el-table--border {
    border: none;
}
.summary-history-table-wrap >>> .el-table--border::after {
    display: none;
}
.summary-history-table-wrap >>> .el-table--border .el-table__cell {
    border-right: none;
}

/* 埋め込み表示 */
.summary-back-to-list-button {
    margin-right: auto;
}
.summary-expand-icon {
    font-size: 16px;
    color: #333B48;
}
.summary-latest-root {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
}
.summary-detail-panel {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    padding: 10px 15px 12px;
    box-sizing: border-box;
    overflow-y: auto;
}
/* パネルの残り高さいっぱいに広げる（ダイアログ用の固定 220px を上書き） */
.summary-latest-text {
    flex: 1;
    height: auto;
    min-height: 120px;
}
.summary-latest-title-row {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    gap: 8px;
}
.summary-latest-title-row .detail-title-wrap {
    flex: 1;
    min-width: 0;
}
/* 全体 CSS の button.el-button--primary が背景色を !important で固定していて Element 標準の
   非活性色が効かないため、非活性は透明度で示す（dialog-ok-button の非活性表現に合わせる） */
.summary-latest-title-row >>> .el-button.is-disabled,
.summary-latest-title-row >>> .el-button.is-disabled:hover,
.summary-latest-title-row >>> .el-button.is-disabled:focus {
    opacity: 0.3;
    cursor: not-allowed;
}

/* 一覧モードの詳細ダイアログ。small-panel(500px) では本文が読みづらいので横幅と本文の高さを広げる */
.summary-detail-dialog {
    width: 760px;
    margin-left: -380px;
}
.summary-detail-dialog .summary-detail-text {
    height: 360px;
}
.summary-latest-result-header {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
}
.summary-latest-result-header .readonly-label {
    margin-bottom: 0;
}
.summary-dialog-button-field {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-left: 20px;
}

.detail-title-row {
    padding: 2px 2px 8px;
}
.detail-title-wrap {
    position: relative;
    display: flex;
    align-items: center;
}
.detail-title-input {
    width: 100%;
    box-sizing: border-box;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 8px 34px 8px 10px;
    font-size: 16px;
    font-weight: bold;
    color: #333b48;
    background-color: transparent;
    transition: background-color .15s ease, border-color .15s ease;
}
.detail-title-wrap:hover .detail-title-input {
    background-color: #f6f6f6;
}
.detail-title-input:focus {
    background-color: #ffffff;
    border-color: #5171cb;
    outline: none;
    box-shadow: 0 0 0 2px #ecf1fa;
}
.detail-title-edit-icon {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    width: 14px;
    height: 14px;
    opacity: 0.4;
    pointer-events: none;
    transition: opacity .15s ease;
}
.detail-title-wrap:hover .detail-title-edit-icon,
.detail-title-input:focus + .detail-title-edit-icon {
    opacity: 0.7;
}
.summary-detail-meta {
    color: #848991;
    font-size: 12px;
    margin: 0 0 10px;
}
.speaker-accordion {
    margin-bottom: 14px;
}
/* 自前の下三角/上三角アイコンに差し替えるため、Element標準の矢印(右向き回転)は隠す */
.speaker-accordion >>> .el-collapse-item__arrow {
    display: none;
}
.speaker-accordion >>> .el-collapse-item__header {
    display: flex;
    align-items: center;
    padding: 0 8px;
    border-radius: 4px;
}
/* 行全体がクリック対象であることを hover で示す（当たり判定自体は Element がヘッダ全幅に持っている） */
.speaker-accordion >>> .el-collapse-item__header:hover {
    background-color: #f6f6f6;
}
.speaker-accordion-icon {
    margin-left: auto;
    color: #848991;
}
.summary-prompt-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
}
.summary-prompt-text {
    margin: 0;
    padding: 8px;
    max-height: 160px;
    overflow-y: auto;
    box-sizing: border-box;
    background-color: #f6f6f6;
    border: 1px solid #eeeeee;
    border-radius: 4px;
    color: #333b48;
    font-family: inherit;
    font-size: 12px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-break: break-all;
}
.summary-prompt-missing {
    color: #848991;
    font-size: 12px;
}
.speaker-chip {
    display: inline-block;
    font-size: 11.5px;
    color: #5171cb;
    background-color: #ecf1fa;
    border-radius: 999px;
    padding: 3px 10px;
    margin: 0 6px 6px 0;
}
.readonly-label {
    font-size: 11px;
    color: #8f94a1;
    margin-bottom: 6px;
}
.summary-detail-text {
    width: 100%;
    box-sizing: border-box;
    height: 220px;
    resize: none;
    overflow-y: auto;
    border: 1px dashed #c7cbd6;
    border-radius: 4px;
    background-color: #f7f8fb;
    padding: 10px;
    font-family: inherit;
    font-size: 13px;
    color: #4a4a4a;
    line-height: 1.6;
    white-space: pre-wrap;
    cursor: default;
}
.copy-row {
    margin-top: 10px;
    text-align: right;
}
</style>
